import React, { useState, useEffect, useCallback } from 'react';
import { getScheduleChangeLog, getScheduleChangeSummary, getTaskHistory } from '../api';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import LoadingState from './LoadingState';
import StateScreen from './ui/StateScreen';
import SegmentedControl from './ui/SegmentedControl';
import {
  SlIcon, fmtTime, fmtVal, fieldMeta, fieldSoft, fieldRing,
  FieldChips, FieldBadge, Timeline, SlippageTable, CountChip, Card, CardTitle, EmptyInline,
} from './schedulelog/ScheduleLogShared';

/* ═══════════════════════════════════════════════════════════════════════════
   Schedule Change Log — By Project log (project-detail tab)
   Embedded in ScheduleLogPage's "By Project" mode. Token-only colors.
   Visual reskin — API + filter/sort + lazy task history unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const VIEWS = [
  { value: 'timeline', label: 'Timeline' },
  { value: 'task', label: 'By Task' },
  { value: 'slippage', label: 'Slippage Report' },
];

const ClearFilterChip = ({ field, onClear }) => {
  const f = fieldMeta(field);
  return (
    <button type="button" className="sl-clearfilter" onClick={onClear} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 26, padding: '0 6px 0 10px', borderRadius: 999, background: fieldSoft(f), border: `1px solid ${fieldRing(f)}`, fontSize: 13, fontWeight: 600, color: f.text, cursor: 'pointer' }}>
      Filter: {field}
      <span style={{ display: 'inline-flex', width: 17, height: 17, borderRadius: 999, alignItems: 'center', justifyContent: 'center', background: `color-mix(in oklch, ${f.tone} 22%, transparent)` }}>
        <SlIcon name="x" size={11} sw={2} />
      </span>
    </button>
  );
};

const TaskRow = ({ projectId, task, open, onToggle }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && history === null && !loading) {
      let cancelled = false;
      setLoading(true);
      getTaskHistory(projectId, task.task_id)
        .then((h) => { if (!cancelled) setHistory(Array.isArray(h) ? h : []); })
        .catch(() => { if (!cancelled) setHistory([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
    return undefined;
    // eslint-disable-next-line
  }, [open]);

  const range = `${fmtTime(task.first_change).split(' · ')[0]} – ${fmtTime(task.last_change).split(' · ')[0]}`;

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)' }}>
      <button type="button" className="sl-taskhead" onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', background: open ? 'var(--bg-surface-2)' : 'transparent', border: 'none' }}>
        <span style={{ display: 'inline-flex', color: 'var(--text-3)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 140ms' }}><SlIcon name="chevron-r" size={15} /></span>
        <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', whiteSpace: 'nowrap' }}>{task.task_code || '—'}</span>
        <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.task_name}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 9px', borderRadius: 999, flex: '0 0 auto', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)' }}>
          <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{task.change_count}</span> changes
        </span>
        <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', whiteSpace: 'nowrap', minWidth: 168, textAlign: 'right' }}>{range}</span>
      </button>

      {open && (
        <div style={{ padding: '4px 16px 16px 43px', background: 'var(--bg-surface-2)' }}>
          <div style={{ borderLeft: '2px solid var(--border-default)', paddingLeft: 16 }}>
            {loading || history === null ? (
              <LoadingState message="Loading task history…" padding="14px 0" />
            ) : history.length === 0 ? (
              <div style={{ padding: '14px 0', fontSize: 14, color: 'var(--text-3)' }}>No recorded changes for this task.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                {history.map((h, i) => (
                  <div key={h.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', minWidth: 150 }}>{fmtTime(h.changed_at)}</span>
                    <FieldBadge field={h.field_changed} />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono" style={{ fontSize: 13.5, color: 'var(--text-3)', textDecoration: 'line-through', textDecorationColor: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(h.field_changed, h.old_value)}</span>
                      <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}><SlIcon name="arrow-r" size={13} sw={1.8} /></span>
                      <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{fmtVal(h.field_changed, h.new_value)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ScheduleLogTab = ({ projectId }) => {
  // The top bar's search (lib/pageSearch): task code / name, and on the timeline the field + values.
  const search = usePageSearch('Search schedule changes…');
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline');
  const [fieldFilter, setFieldFilter] = useState(null);
  const [expandedTask, setExpandedTask] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fieldFilter) params.field = fieldFilter;
      const [logData, summaryData] = await Promise.all([
        getScheduleChangeLog(projectId, params),
        getScheduleChangeSummary(projectId),
      ]);
      setLogs(Array.isArray(logData) ? logData : []);
      setSummary(summaryData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [projectId, fieldFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !summary) return <LoadingState message="Loading change log…" padding="80px 20px" />;

  const totalChanges = summary?.totalChanges || 0;
  const byField = summary?.byField || [];
  const byTask = summary?.byTask || [];
  const slippage = summary?.slippage || [];
  const shownLogs = logs.filter((e) => matchesSearch(search, e.task_code, e.task_name, e.field_changed,
    e.old_value, e.new_value, e.changed_by_name, e.user_name));
  const shownTasks = byTask.filter((t) => matchesSearch(search, t.task_code, t.task_name));
  const shownSlips = slippage.filter((r) => matchesSearch(search, r.task_code, r.task_name));
  const noMatch = <EmptyInline {...noMatchText(search)} />;

  if (!summary || totalChanges === 0) {
    return (
      <Card>
        <EmptyInline title="No schedule changes yet" body="Changes appear here as the PM updates task dates, durations, and progress." />
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes slSpin { to { transform: rotate(360deg); } }
        .sl-fieldchip:hover { border-color: var(--border-strong); background: var(--bg-hover); }
        .sl-taskhead:hover { background: var(--bg-hover) !important; }
        .sl-clearfilter:hover { filter: brightness(0.97); }
        .sl-select:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <CountChip>
          <span className="mono" style={{ fontWeight: 700, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{totalChanges}</span> total changes recorded
        </CountChip>
      </div>

      {byField.length > 0 && <FieldChips byField={byField} fieldFilter={fieldFilter} onFieldFilter={setFieldFilter} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <SegmentedControl value={view} onChange={setView} options={VIEWS} />
        <div style={{ flex: 1 }} />
      </div>

      {view === 'timeline' && (
        <Card
          header={
            <>
              <CardTitle>Change timeline</CardTitle>
              {fieldFilter && <ClearFilterChip field={fieldFilter} onClear={() => setFieldFilter(null)} />}
              <div style={{ flex: 1 }} />
              <CountChip>{shownLogs.length} {shownLogs.length === 1 ? 'change' : 'changes'}</CountChip>
            </>
          }
        >
          {logs.length === 0 ? (
            <EmptyInline title="No changes for this field" body="No changes to this field have been recorded for this project. Clear the filter to see everything." />
          ) : shownLogs.length === 0 ? noMatch : (
            <Timeline events={shownLogs} />
          )}
        </Card>
      )}

      {view === 'task' && (
        <Card pad={false} header={<><CardTitle>Change history by task</CardTitle><div style={{ flex: 1 }} /><CountChip>{shownTasks.length} tasks</CountChip></>}>
          <div>
            {byTask.length > 0 && shownTasks.length === 0 && <div style={{ padding: 16 }}>{noMatch}</div>}
            {shownTasks.map((t) => (
              <TaskRow key={t.task_id} projectId={projectId} task={t} open={expandedTask === t.task_id} onToggle={() => setExpandedTask((cur) => (cur === t.task_id ? null : t.task_id))} />
            ))}
          </div>
        </Card>
      )}

      {view === 'slippage' && (
        <Card pad={false} header={<><CardTitle>Finish-date slippage report</CardTitle><div style={{ flex: 1 }} /><CountChip>{shownSlips.length} slips</CountChip></>}>
          {slippage.length === 0 ? (
            <StateScreen kind="empty" title="No finish date slippage recorded" body="No tasks on this project have had their finish dates pushed out." />
          ) : shownSlips.length === 0 ? <div style={{ padding: 16 }}>{noMatch}</div> : (
            <SlippageTable rows={shownSlips} />
          )}
        </Card>
      )}
    </div>
  );
};

export default ScheduleLogTab;
